// Basic
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
// Store & actions
import useContractStore from '@/app/store/modules/contract';
import actionsContracts from '@/app/modules/contracts/api/actionsContracts';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import ContractsTable from '@/app/modules/contracts/components/tables/ContractsTable';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsAccount } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.merchantAccountDetails
 * @returns {JSX} ModalDeleteMultipleContracts
 */
function ModalDeleteMultipleContracts({ isOpen, setIsOpen, merchantAccountDetails }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.delete_multiple_contracts.steps';

    // Store
    const storeRefetchContracts = useContractStore(s => s.storeRefetchContracts);

    // Toast
    const { toast } = useToast();

    // URL Params
    const { contractAccessId: merchantAccountId } = useParams();

    // State
    const [step, setStep] = useState(0);
    const [contractsSelected, setContractsSelected] = useState(getInitialValueForRowSelected());

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchContracts(true);
        toast({
            description: <Entity namespace={nsAccount} entity='toast.success.delete_multiple_contracts' />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsAccount} entity='toast.error.delete_multiple_contracts' />,
            variant: 'danger'
        });
    };
    const deleteMultipleContracts = actionsContracts.deleteMultipleContracts(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext, modalSize } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.contracts_selection`,
                disabledNext: !Object.keys(contractsSelected.rowIds).length,
                modalSize: 'max-w-4xl'
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirm`,
                disabledNext: false,
                modalSize: 'max-w-2xl'
            }
        }),
        [contractsSelected]
    );

    return <Modal open={isOpen} onOpenChange={setIsOpen}>
        <ModalContent className={modalSize}>
            <HeaderTitle title='modal.delete_multiple_contracts.title' namespace={nsAccount} />
            <ModalBody className='modal-max-height overflow-auto'>
                <Entity
                    entity={stepTitle}
                    data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                    namespace={nsAccount}
                    className={'mb-2 flex'}
                />
                {step === 0 &&
                    <ContractsTable
                        filtersEventName={setFiltersEventName('contracts', true)}
                        merchantAccountId={merchantAccountId}
                        rowsSelected={contractsSelected}
                        setRowsSelected={setContractsSelected}
                    />
                }
                {step === 1 &&
                    <div className='text-center space-y-4'>
                        <div>
                            <Entity
                                entity='modal.delete_multiple_contracts.confirmation.text_1'
                                namespace={nsAccount}
                                data={{ number: contractsSelected.rowsData.length, label: merchantAccountDetails.label }}
                            />
                        </div>
                        <div>
                            <Entity
                                entity='modal.delete_multiple_contracts.confirmation.text_2' namespace={nsAccount} />
                        </div>
                    </div>
                }
            </ModalBody>
            <FooterStep
                step={step}
                setStep={setStep}
                stepsNumber={STEPS_NUMBER}
                onValidate={() => deleteMultipleContracts.mutate({
                    merchantAccountId: merchantAccountId,
                    contractsIds: Object.keys(contractsSelected.rowIds)
                })}
                isLoading={deleteMultipleContracts.isLoading}
                disableValidation={disabledNext}
            />
        </ModalContent>
    </Modal>;
}

ModalDeleteMultipleContracts.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    merchantAccountDetails: PropTypes.object
};

export default ModalDeleteMultipleContracts;